import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Image as ImageIcon, Activity, Map } from 'lucide-react';
const pictogramImages = ['https://cdn.magicpatterns.com/uploads/w9rHubuT5hK4sQNxT9KSwW/Tomo-1-EL-DICCIONARIO-VISUAL_(2).webp', 'https://cdn.magicpatterns.com/uploads/pBvpZcsqtC7RzVkWycV9ym/Tomo-1-EL-DICCIONARIO-VISUAL.webp', 'https://cdn.magicpatterns.com/uploads/9LxZpzZgKDrwrd1fdWe7eV/Tomo-1-EL-DICCIONARIO-VISUAL_(1).webp', 'https://cdn.magicpatterns.com/uploads/4Y1J3NHEhXb1Mtg1SVK63P/Tomo-1-EL-DICCIONARIO-VISUAL_(3).webp', 'https://cdn.magicpatterns.com/uploads/iKKi4i37daF6BP5waU3hDo/Tomo-1-EL-DICCIONARIO-VISUAL_(4).webp', 'https://cdn.magicpatterns.com/uploads/4YTCRkAqT4Zxdd3XfMyShY/Tomo-1-EL-DICCIONARIO-VISUAL_(5).webp'];
const historiasImages = ['https://cdn.magicpatterns.com/uploads/wnJwptUFyScXNSd4jvjoUn/Tomo-4-HISTORIAS-SOCIALES.webp', 'https://cdn.magicpatterns.com/uploads/e2N2t3Gbi1Q35zF8rKwtQF/Tomo-4-HISTORIAS-SOCIALES_(1).webp', 'https://cdn.magicpatterns.com/uploads/k2o41oVaRVENSAX2BvYMev/Tomo-4-HISTORIAS-SOCIALES_(2).webp', 'https://cdn.magicpatterns.com/uploads/otAJkNRUcMbzUuR1JPsuSQ/Tomo-4-HISTORIAS-SOCIALES_(3).webp'];
type CarouselProps = {
  images: string[];
  altPrefix: string;
  bgClass: string;
  dotActiveClass: string;
  dotInactiveClass: string;
  interval?: number;
};
function Carousel({
  images,
  altPrefix,
  bgClass,
  dotActiveClass,
  dotInactiveClass,
  interval = 3000
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % images.length);
    }, interval);
    return () => clearInterval(timer);
  }, [images.length, interval]);
  const variants = {
    initial: {
      opacity: 0,
      x: shouldReduceMotion ? 0 : 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: shouldReduceMotion ? 0 : -20
    }
  };
  return <div className={`relative w-full h-72 md:h-80 ${bgClass} rounded-xl overflow-hidden mb-6 flex flex-col`} data-id="element-295">
      <div className="relative flex-grow overflow-hidden" data-id="element-296">
        <AnimatePresence mode="wait" data-id="element-297">
          <motion.img key={currentIndex} src={images[currentIndex]} alt={`${altPrefix} — ${currentIndex + 1}`} width={800} height={1131} loading="lazy" decoding="async" variants={variants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.3
        }} className="absolute inset-0 w-full h-full object-contain p-2" data-id="element-298" />
        </AnimatePresence>
      </div>
      <div className="flex justify-center gap-1.5 pb-3" data-id="element-299">
        {images.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIndex ? dotActiveClass : dotInactiveClass}`} aria-hidden="true" data-id="element-300" />)}
      </div>
    </div>;
}
const methods = [{
  icon: ImageIcon,
  tomo: 'TOMO 1 · El Diccionario Visual',
  title: 'Los 100 Pictogramas Base',
  color: 'bg-teal-50 border-teal-100',
  iconColor: 'text-teal-600 bg-teal-200',
  desc: 'Una biblioteca visual completa con las imágenes que tu hijo necesita para comunicar sus necesidades, emociones y deseos desde el primer día.',
  renderImage: () => <Carousel images={pictogramImages} altPrefix="Pictogramas del Diccionario Visual — ficha" bgClass="bg-teal-100/50" dotActiveClass="bg-teal-500" dotInactiveClass="bg-teal-200" data-id="element-301" />
}, {
  icon: Activity,
  tomo: 'TOMO 3 · Control de la Ansiedad',
  title: 'El Sistema de Semáforo Emocional',
  color: 'bg-amber-50 border-amber-100',
  iconColor: 'text-amber-600 bg-amber-200',
  desc: 'Una herramienta visual para que tu hijo identifique y comunique cómo se siente antes de explotar. Verde, Amarillo, Rojo. Simple. Efectivo. Revolucionario.',
  renderImage: () => <div className="w-full h-72 md:h-80 bg-amber-100/50 rounded-xl mb-6 overflow-hidden flex items-center justify-center p-2" data-id="element-302">
        <img src="https://cdn.magicpatterns.com/uploads/35akHxyHmkC8mQvx3WKW9a/Tomo-3-CONTROL-DE-LA-ANSIEDAD.webp" alt="Semáforo Emocional" width={800} height={1131} loading="lazy" decoding="async" className="w-full h-full object-contain drop-shadow-sm" data-id="element-303" />
      </div>
}, {
  icon: Map,
  tomo: 'TOMO 4 · Historias Sociales',
  title: 'El Mapa del Mundo Exterior',
  color: 'bg-coral-50 border-coral-100',
  iconColor: 'text-coral-600 bg-coral-200',
  desc: 'Guías visuales paso a paso para preparar el cerebro de tu hijo para el médico, el supermercado, los cumpleaños y la peluquería. Para que vuelvan a salir juntos sin miedo.',
  renderImage: () => <Carousel images={historiasImages} altPrefix="Historia social del médico — parte" bgClass="bg-coral-100/50" dotActiveClass="bg-coral-500" dotInactiveClass="bg-coral-200" data-id="element-304" />
}];
export function VisualMethods() {
  return <section className="py-12 md:py-16 bg-white" data-id="element-305">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-306">
        <div className="text-center mb-8" data-id="element-307">
          <span className="text-amber-600 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-308">
            Mirá adentro del sistema
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-teal-900" data-id="element-309">
            Un vistazo a los Métodos Visuales
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto" data-id="element-310">
            Herramientas simples que los niños pueden entender desde el primer
            día.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-311">
          {methods.map((m, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`rounded-2xl border ${m.color} flex flex-col overflow-hidden p-6`} data-id="element-312">
              {m.renderImage()}

              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${m.iconColor}`} data-id="element-313">
                <m.icon className="w-7 h-7" data-id="element-314" />
              </div>
              <div className="text-xs font-bold text-stone-500 uppercase tracking-wide mb-1.5" data-id="element-315">
                {m.tomo}
              </div>
              <h3 className="text-xl font-bold text-teal-900 mb-3" data-id="element-316">
                {m.title}
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed flex-grow" data-id="element-317">
                {m.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}